import React from 'react';

export const updated = 'October 16, 2026';

const privacyContentEn = (
  <>
    <h2>Privacy Policy</h2>
    <p><strong>Last Updated:</strong> {updated}</p>

    <p>
      OnyxUP ("we", "us", "our"), developed by Halil Yılmaz, respects the privacy of users of the Habit Branches
      mobile application (the "App"). This Privacy Policy explains what data the App collects, how it is used, and
      your rights.
    </p>

    <h2>1. Data We Collect</h2>
    <p>
      Habit Branches is designed to work entirely on your device. There is no account, login, or cloud sync. Your
      goals, habits, streaks, and tree progress are stored only on your device and are never transmitted to or
      stored on our servers.
    </p>
    <ul>
      <li><strong>Device and diagnostic data</strong> (device model, OS version, crash logs, if a crash-reporting tool is enabled) — Troubleshooting and improving App stability.</li>
      <li><strong>Notification permission</strong> (if you enable habit reminders) — Scheduling local reminder notifications on your device. This is handled entirely by your device's operating system; we do not receive or store this data.</li>
    </ul>
    <p>We do not collect your name, email address, photos, or location.</p>

    <h2>2. How Data Is Shared</h2>
    <p>
      Since your habit and goal data never leaves your device, we have nothing to share. Anonymous crash/diagnostic
      data, if collected, may be processed by the service providers listed below solely to help us fix bugs and
      improve the App.
    </p>
    <p>We do not sell your data to third parties for advertising purposes.</p>

    <h2>3. Third-Party Services Used</h2>
    <ul>
      <li><strong>Apple</strong> (App Store) — App distribution only.</li>
    </ul>

    <h2>4. Data Retention</h2>
    <p>
      Your habit and goal data stays on your device for as long as the App is installed. Deleting the App removes
      all of this data from your device; since nothing is stored on our servers, there is nothing further for us to
      delete.
    </p>

    <h2>5. Your Rights</h2>
    <ul>
      <li>Since no personal data is stored on our servers, there is no server-side data for us to provide, correct, or delete.</li>
      <li>You can remove all of your App data at any time by deleting the App from your device.</li>
      <li>If you have any privacy-related questions, you can contact us at any time.</li>
    </ul>
    <p>You can reach us at <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>

    <h2>6. Children's Privacy</h2>
    <p>
      The App does not knowingly collect personal data from children under the age of 13 (or the applicable minimum
      age in your region). Because the App stores data only on-device and collects no personal information, this
      risk is minimal.
    </p>

    <h2>7. Data Security</h2>
    <p>
      Because your data is stored locally on your device, its security depends on your device's own security
      measures (passcode, encryption, etc.). We recommend keeping your device's operating system up to date.
    </p>

    <h2>8. Regional Differences</h2>
    <p>The App functions consistently across all regions, with no region-specific differences in data processing practices.</p>

    <h2>9. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. Changes will be posted on this page, and the "Last
      updated" date will be revised accordingly.
    </p>

    <h2>10. Contact</h2>
    <p>Privacy questions can be sent to: <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>
  </>
);

const termsContentEn = (
  <>
    <h2>Terms of Use</h2>
    <p><strong>Last Updated:</strong> {updated}</p>

    <p>
      These Terms of Use ("Terms") govern your access to and use of the Habit Branches mobile application (the
      "App"), provided by OnyxUP ("we", "us", "our"), developed by Halil Yılmaz. By downloading, installing, or
      using the App, you agree to be bound by these Terms.
    </p>

    <h2>1. Description of the App</h2>
    <p>
      Habit Branches helps you turn goals into daily habits. As you stay consistent, a tree grows branch by branch on
      your device, giving you a visual way to track your streaks and progress over time. The App works fully
      on-device and does not require an account.
    </p>

    <h2>2. No Account; Local Data Only</h2>
    <ul>
      <li>The App does not require you to create an account or sign in.</li>
      <li>All of your goals, habits, streaks, and progress are stored locally on your device only.</li>
      <li>We do not back up your data to our servers. If you uninstall the App, lose your device, or reset it without an OS-level device backup, your App data cannot be recovered by us.</li>
    </ul>

    <h2>3. Acceptable Use</h2>
    <p>
      You agree to use the App only for its intended purpose of personal goal and habit tracking, and not to
      reverse-engineer, tamper with, or misuse the App in any way that could harm its functionality for you or other
      users.
    </p>

    <h2>4. Intellectual Property</h2>
    <p>
      All design, code, graphics, and branding within the App belong to OnyxUP or are used under license. You retain
      ownership of any content you create within the App (such as named goals or habits), which remains stored only
      on your device.
    </p>

    <h2>5. Disclaimer of Warranties; Limitation of Liability</h2>
    <p>
      The App is provided "as is." We do not warrant that the App will operate uninterrupted or error-free, or that
      your locally stored data will never be lost (for example, due to device loss, OS issues, or app deletion). To
      the maximum extent permitted by law, we are not liable for any indirect, incidental, or consequential damages
      arising from your use of the App, including loss of locally stored data.
    </p>

    <h2>6. Changes to These Terms</h2>
    <p>We may update these Terms from time to time. Material changes will be announced via an in-app notice or an updated "Last updated" date.</p>

    <h2>7. Governing Law</h2>
    <p>These Terms are governed by the laws of the Republic of Türkiye, without regard to conflict-of-law principles.</p>

    <h2>8. Contact</h2>
    <p>Questions about these Terms can be sent to: <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>

    <p><em>See also: Apple's Standard End User License Agreement (EULA), which applies to this App in addition to these Terms.</em></p>
  </>
);

const privacyContentTr = (
  <>
    <h2>Gizlilik Politikası</h2>
    <p><strong>Son Güncelleme:</strong> {updated}</p>

    <p>
      Halil Yılmaz tarafından geliştirilen OnyxUP ("biz", "bizim"), Habit Branches mobil uygulamasının ("Uygulama")
      kullanıcılarının gizliliğine değer verir. Bu Gizlilik Politikası, Uygulamanın hangi verileri topladığını, bunları
      nasıl kullandığını ve haklarınızı açıklar.
    </p>

    <h2>1. Topladığımız Veriler</h2>
    <p>
      Habit Branches tamamen cihazınızda çalışacak şekilde tasarlanmıştır. Hesap, giriş veya bulut senkronizasyonu
      yoktur. Hedefleriniz, alışkanlıklarınız, serileriniz ve ağaç ilerlemeniz yalnızca cihazınızda saklanır ve asla
      sunucularımıza iletilmez veya orada saklanmaz.
    </p>
    <ul>
      <li><strong>Cihaz ve tanılama verileri</strong> (cihaz modeli, işletim sistemi sürümü, çökme kayıtları — bir çökme raporlama aracı etkinse) — Uygulama kararlılığını giderme ve iyileştirme.</li>
      <li><strong>Bildirim izni</strong> (alışkanlık hatırlatıcılarını etkinleştirirseniz) — Cihazınızda yerel hatırlatma bildirimleri planlama. Bu tamamen cihazınızın işletim sistemi tarafından yönetilir; bu veriyi almayız veya saklamayız.</li>
    </ul>
    <p>Adınızı, e-posta adresinizi, fotoğraflarınızı veya konumunuzu toplamayız.</p>

    <h2>2. Veriler Nasıl Paylaşılır</h2>
    <p>
      Alışkanlık ve hedef verileriniz cihazınızdan hiçbir zaman ayrılmadığından, paylaşacak bir şeyimiz yoktur.
      Toplanıyorsa, anonim çökme/tanılama verileri yalnızca hataları düzeltmemize ve Uygulamayı iyileştirmemize
      yardımcı olmak amacıyla aşağıda listelenen hizmet sağlayıcıları tarafından işlenebilir.
    </p>
    <p>Verilerinizi reklam amacıyla üçüncü taraflara satmayız.</p>

    <h2>3. Kullanılan Üçüncü Taraf Hizmetleri</h2>
    <ul>
      <li><strong>Apple</strong> (App Store) — Yalnızca uygulama dağıtımı.</li>
    </ul>

    <h2>4. Veri Saklama</h2>
    <p>
      Alışkanlık ve hedef verileriniz, Uygulama cihazınızda yüklü olduğu sürece cihazınızda kalır. Uygulamayı
      silmek bu verilerin tamamını cihazınızdan kaldırır; sunucularımızda hiçbir şey saklanmadığından, bizim
      tarafımızdan silinecek başka bir şey yoktur.
    </p>

    <h2>5. Haklarınız</h2>
    <ul>
      <li>Sunucularımızda kişisel veri saklanmadığından, sağlayacağımız, düzelteceğimiz veya sileceğimiz sunucu tarafı bir veri yoktur.</li>
      <li>Uygulamayı cihazınızdan silerek tüm Uygulama verilerinizi istediğiniz zaman kaldırabilirsiniz.</li>
      <li>Gizlilikle ilgili sorularınız varsa, bizimle istediğiniz zaman iletişime geçebilirsiniz.</li>
    </ul>
    <p>Bize <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a> adresinden ulaşabilirsiniz</p>

    <h2>6. Çocukların Gizliliği</h2>
    <p>
      Uygulama, 13 yaşın altındaki çocuklardan (veya bölgenizdeki geçerli asgari yaştan) bilerek kişisel veri
      toplamaz. Uygulama verileri yalnızca cihaz üzerinde sakladığından ve kişisel bilgi toplamadığından, bu risk
      asgari düzeydedir.
    </p>

    <h2>7. Veri Güvenliği</h2>
    <p>
      Verileriniz cihazınızda yerel olarak saklandığından, güvenliği cihazınızın kendi güvenlik önlemlerine (parola,
      şifreleme vb.) bağlıdır. Cihazınızın işletim sistemini güncel tutmanızı öneririz.
    </p>

    <h2>8. Bölgesel Farklar</h2>
    <p>Uygulama tüm bölgelerde tutarlı şekilde çalışır; veri işleme uygulamalarında bölgeye özel bir fark yoktur.</p>

    <h2>9. Bu Politikadaki Değişiklikler</h2>
    <p>
      Bu Gizlilik Politikasını zaman zaman güncelleyebiliriz. Değişiklikler bu sayfada yayınlanacak ve "Son
      güncelleme" tarihi buna göre revize edilecektir.
    </p>

    <h2>10. İletişim</h2>
    <p>Gizlilik soruları şu adrese gönderilebilir: <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>
  </>
);

const termsContentTr = (
  <>
    <h2>Kullanım Şartları</h2>
    <p><strong>Son Güncelleme:</strong> {updated}</p>

    <p>
      Bu Kullanım Şartları ("Şartlar"), Halil Yılmaz tarafından geliştirilen OnyxUP ("biz", "bizim") tarafından
      sağlanan Habit Branches mobil uygulamasına ("Uygulama") erişiminizi ve kullanımınızı düzenler. Uygulamayı
      indirerek, kurarak veya kullanarak bu Şartlara bağlı kalmayı kabul edersiniz.
    </p>

    <h2>1. Uygulamanın Tanımı</h2>
    <p>
      Habit Branches, hedefleri günlük alışkanlıklara dönüştürmenize yardımcı olur. Düzenli kaldıkça, cihazınızda
      dal dal büyüyen bir ağaç, serilerinizi ve ilerlemenizi zaman içinde görsel olarak takip etmenizi sağlar.
      Uygulama tamamen cihaz üzerinde çalışır ve hesap gerektirmez.
    </p>

    <h2>2. Hesap Yok; Yalnızca Yerel Veri</h2>
    <ul>
      <li>Uygulama, hesap oluşturmanızı veya giriş yapmanızı gerektirmez.</li>
      <li>Tüm hedefleriniz, alışkanlıklarınız, serileriniz ve ilerlemeniz yalnızca cihazınızda yerel olarak saklanır.</li>
      <li>Verilerinizi sunucularımıza yedeklemeyiz. Uygulamayı kaldırırsanız, cihazınızı kaybederseniz veya işletim sistemi düzeyinde bir cihaz yedeği olmadan sıfırlarsanız, Uygulama verileriniz tarafımızca kurtarılamaz.</li>
    </ul>

    <h2>3. Kabul Edilebilir Kullanım</h2>
    <p>
      Uygulamayı yalnızca kişisel hedef ve alışkanlık takibi amacıyla kullanmayı, Uygulamayı tersine mühendislikle
      çözmemeyi, kurcalamamayı veya kendiniz ya da diğer kullanıcılar için işlevselliğine zarar verecek şekilde kötüye
      kullanmamayı kabul edersiniz.
    </p>

    <h2>4. Fikri Mülkiyet</h2>
    <p>
      Uygulama içindeki tüm tasarım, kod, grafik ve marka öğeleri OnyxUP'a aittir veya lisans altında kullanılır.
      Uygulama içinde oluşturduğunuz içeriğin (adlandırılmış hedefler veya alışkanlıklar gibi) mülkiyetini elinde
      tutarsınız; bu içerik yalnızca cihazınızda saklı kalır.
    </p>

    <h2>5. Garanti Feragati; Sorumluluğun Sınırlandırılması</h2>
    <p>
      Uygulama "olduğu gibi" sağlanır. Uygulamanın kesintisiz veya hatasız çalışacağını ya da yerel olarak
      saklanan verilerinizin asla kaybolmayacağını (örneğin cihaz kaybı, işletim sistemi sorunları veya uygulama
      silinmesi nedeniyle) garanti etmeyiz. Yasaların izin verdiği azami ölçüde, Uygulamayı kullanımınızdan
      kaynaklanan, yerel olarak saklanan veri kaybı dahil, hiçbir dolaylı, arızi veya sonuç olarak ortaya çıkan
      zarardan sorumlu değiliz.
    </p>

    <h2>6. Bu Şartlardaki Değişiklikler</h2>
    <p>Bu Şartları zaman zaman güncelleyebiliriz. Önemli değişiklikler uygulama içi bir bildirim veya güncellenmiş bir "Son güncelleme" tarihiyle duyurulacaktır.</p>

    <h2>7. Geçerli Hukuk</h2>
    <p>Bu Şartlar, kanunlar ihtilafı kurallarına bakılmaksızın Türkiye Cumhuriyeti yasalarına göre yönetilir.</p>

    <h2>8. İletişim</h2>
    <p>Bu Şartlar hakkında sorularınız şu adrese gönderilebilir: <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>

    <p><em>Ayrıca bakınız: Bu Şartlara ek olarak bu Uygulamaya uygulanan Apple'ın Standart Son Kullanıcı Lisans Sözleşmesi (EULA).</em></p>
  </>
);

export const en = { privacyContent: privacyContentEn, termsContent: termsContentEn };
export const tr = { privacyContent: privacyContentTr, termsContent: termsContentTr };
